import { Heart, Rocket, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useFavorites } from "@/context/FavoritesContext";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { CountdownTimer } from "@/components/CountdownTimer";
import { EmptyState } from "@/components/States";
import { toast } from "sonner";

export default function WatchlistPage() {
  const { favorites, removeFavorite } = useFavorites();

  const handleRemove = (id, name) => {
    removeFavorite(id);
    toast.info(`Removed ${name} from watchlist`);
  };

  return (
    <div className="min-h-screen" data-testid="watchlist-page">
      <div className="hero-gradient fixed inset-0 pointer-events-none" />
      
      <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        {/* Header */}
        <PageHeader 
          icon={Heart}
          title="My Watchlist"
          subtitle="Your saved launches for quick access"
        />

        {favorites.length === 0 ? (
          <EmptyState
            title="No Launches Saved"
            message="Tap the heart icon on any launch to add it to your watchlist."
            icon={Heart}
          />
        ) : (
          <div className="space-y-4">
            {favorites.map((launch, index) => (
              <div
                key={launch.id}
                className="glass-card p-4 md:p-6 stagger-item"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`watchlist-item-${launch.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/launches/${launch.id}`}
                      className="block group"
                    >
                      <h3 className="font-heading font-bold text-lg text-white group-hover:text-cosmic-blue-glow"
                          style={{ transition: "color 0.2s ease" }}>
                        {launch.name}
                      </h3>
                    </Link>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Rocket className="w-4 h-4 text-cosmic-blue" strokeWidth={1.5} />
                        {launch.rocket}
                      </span>
                      <span>•</span>
                      <span>{launch.provider}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{launch.pad_name}</p>
                  </div>
                  
                  <button
                    onClick={() => handleRemove(launch.id, launch.name)}
                    className="p-2 rounded-lg bg-ignition-red/20 border border-ignition-red/30 text-ignition-red hover:bg-ignition-red/30"
                    style={{ transition: "background-color 0.2s ease" }}
                    title="Remove from watchlist"
                    data-testid={`remove-watchlist-${launch.id}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>

                {/* Countdown */}
                {launch.net && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-2 font-heading">
                      T-Minus
                    </p>
                    <CountdownTimer targetDate={launch.net} compact />
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 flex gap-2">
                  <Link
                    to={`/launches/${launch.id}`}
                    className="btn-primary text-sm py-2 px-4"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}
